import React, { useState, useCallback, useMemo } from 'react'
import {
  TextField,
  Typography,
  Alert,
  Tabs,
  Tab,
  Paper,
  Box,
  Divider,
  Chip,
  Card,
  CardContent,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import 'chart.js/auto'
import { apiConfig } from '../../api/ApiConfig.js'
import SpinnerButton from '../../components/common/SpinnerButton.js'
import { useSelector } from 'react-redux'

// ============================================================================
// CONSTANTS
// ============================================================================
const TABS = {
  CALCULATOR: 'calculator',
  PREDICTOR: 'predictor',
  FORM: 'form'
}

const FIELD_LABELS = {
  soluteSmiles: 'Solute SMILES',
  solventSmiles: 'Solvent SMILES',
  temperature: 'Temperature (°C)',
  ph: 'pH',
  deltaHsol: 'ΔH Solution (kJ/mol)',
  pKa: 'pKa',
  SPure: 'Pure Solubility (mg/mL)',
  molecularWeight: 'Molecular Weight (g/mol)',
  const: 'Constant'
}

const TAB_LABELS = {
  [TABS.CALCULATOR]: 'Solubility Calculator',
  [TABS.PREDICTOR]: 'Water Solubility Predictor',
  [TABS.FORM]: 'Advanced Calculator'
}

// ============================================================================
// STYLED COMPONENTS - Professional Space Grey Theme
// ============================================================================
const HeaderContainer = styled(Paper)(({ theme }) => ({
  background: 'linear-gradient(135deg, #8B909A 0%, #6B7280 100%)',
  color: '#FFFFFF',
  padding: theme.spacing(3, 4),
  borderRadius: theme.spacing(2),
  marginBottom: theme.spacing(3),
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
}))

const ContentContainer = styled(Paper)(({ theme }) => ({
  background: '#FFFFFF',
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
  border: '1px solid #E5E7EB',
  overflow: 'hidden',
}))

const TabSection = styled(Box)(({ theme }) => ({
  background: '#F9FAFB',
  padding: theme.spacing(3),
  minHeight: '500px',
}))

const FormCard = styled(Card)(({ theme }) => ({
  background: '#FFFFFF',
  border: '1px solid #E5E7EB',
  borderRadius: theme.spacing(2),
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
  maxWidth: '700px',
  margin: '0 auto',
}))

const ResultCard = styled(Box)(({ theme, severity = 'success' }) => {
  const getResultStyles = () => {
    switch (severity) {
      case 'success':
        return {
          backgroundColor: '#ECFDF5',
          borderColor: '#A7F3D0',
          color: '#065F46'
        }
      case 'info':
        return {
          backgroundColor: '#EFF6FF',
          borderColor: '#BFDBFE',
          color: '#1E40AF'
        }
      case 'warning':
        return {
          backgroundColor: '#FFFBEB',
          borderColor: '#FDE68A',
          color: '#92400E'
        }
      default:
        return {
          backgroundColor: '#ECFDF5',
          borderColor: '#A7F3D0',
          color: '#065F46'
        }
    }
  }
  
  const styles = getResultStyles()
  
  return {
    padding: theme.spacing(2.5),
    borderRadius: theme.spacing(2),
    border: `1px solid ${styles.borderColor}`,
    backgroundColor: styles.backgroundColor,
    color: styles.color,
    marginTop: theme.spacing(2),
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
  }
})

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#FFFFFF',
    '& fieldset': {
      borderColor: '#D1D5DB',
    },
    '&:hover fieldset': {
      borderColor: '#6B7280',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#4B5563',
    }
  },
  '& .MuiInputLabel-root': {
    color: '#6B7280',
    '&.Mui-focused': {
      color: '#4B5563',
    }
  }
}))

const FeatureChip = styled(Chip)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  color: '#FFFFFF',
  fontWeight: 500,
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
  }
}))

const StyledTabs = styled(Tabs)(({ theme }) => ({
  backgroundColor: '#F8FAFC',
  '& .MuiTab-root': {
    color: '#6B7280',
    fontWeight: 500,
    textTransform: 'none',
    fontSize: '1rem',
    '&.Mui-selected': {
      color: '#4B5563',
      fontWeight: 600,
    }
  },
  '& .MuiTabs-indicator': {
    backgroundColor: '#6B7280',
    height: '3px',
    borderRadius: '2px 2px 0 0',
  }
}))

const ActionButton = styled(SpinnerButton)(({ theme }) => ({
  background: 'linear-gradient(135deg, #8B909A 0%, #6B7280 100%)',
  color: '#FFFFFF',
  fontWeight: 600,
  textTransform: 'none',
  borderRadius: theme.spacing(1),
  padding: theme.spacing(1.2, 3),
  '&:hover': {
    background: 'linear-gradient(135deg, #6B7280 0%, #4B5563 100%)',
  },
  '&:disabled': {
    background: '#E5E7EB',
    color: '#9CA3AF',
  }
}))

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

// Initial state factory
const createInitialFormData = () => ({
  soluteSmiles: '',
  solventSmiles: '',
  temperature: '',
  ph: '',
  deltaHsol: '',
  pKa: '',
  SPure: '',
  molecularWeight: '',
  const: '',
})

// Validation utilities
const validateCalculatorInputs = (solute, solvent, temperature) => {
  if (!solute.trim()) return 'Solute SMILES is required'
  if (!solvent.trim()) return 'Solvent SMILES is required'
  if (!temperature.trim()) return 'Temperature is required'
  if (isNaN(parseFloat(temperature))) return 'Temperature must be a valid number'
  return null
}

const validateSmilesInput = (smiles) => {
  if (!smiles.trim()) return 'SMILES string is required'
  // Basic SMILES validation - fixed regex escaping
  if (!/^[A-Za-z0-9@+\-[\]()=#$:./\\%]+$/.test(smiles)) {
    return 'Invalid SMILES format'
  }
  return null
}

const validateFormData = (formData) => {
  const requiredFields = ['soluteSmiles', 'solventSmiles', 'temperature']
  for (const field of requiredFields) {
    if (!formData[field].trim()) {
      return `${FIELD_LABELS[field]} is required`
    }
  }
  
  // Validate numeric fields
  const numericFields = ['temperature', 'ph', 'deltaHsol', 'pKa', 'SPure', 'molecularWeight']
  for (const field of numericFields) {
    if (formData[field] && isNaN(parseFloat(formData[field]))) {
      return `${FIELD_LABELS[field]} must be a valid number`
    }
  }
  
  return null
}

// ============================================================================
// CUSTOM HOOKS
// ============================================================================
const useApiCalls = () => {
  const {
    calculateSolventSoluteSolubility,
    calculateWaterSolubility,
    calculateSolubilityWithThermodynamics,
    logUserEvent
  } = apiConfig()
  
  return {
    calculateSolventSoluteSolubility,
    calculateWaterSolubility,
    calculateSolubilityWithThermodynamics,
    logUserEvent
  }
}

const useSolubilityState = () => {
  const [activeTab, setActiveTab] = useState(TABS.CALCULATOR)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  
  // Calculator tab state
  const [calculatorData, setCalculatorData] = useState({
    solute: '',
    solvent: '',
    temperature: ''
  })
  const [solubility, setSolubility] = useState(null)
  
  // Predictor tab state
  const [smiles, setSmiles] = useState('')
  const [predictedLogS, setPredictedLogS] = useState(null)
  const [predictedLogSEnsemble, setPredictedLogSEnsemble] = useState(null)
  
  // Form tab state
  const [formData, setFormData] = useState(createInitialFormData())
  const [estimatedSolubility, setEstimatedSolubility] = useState(null)
  const [adjustedSolubility, setAdjustedSolubility] = useState(null)
  
  // Clear all states
  const clearAllStates = useCallback(() => {
    setError('')
    setSolubility(null)
    setPredictedLogS(null)
    setPredictedLogSEnsemble(null)
    setEstimatedSolubility(null)
    setAdjustedSolubility(null)
    setSmiles('')
    setFormData(createInitialFormData())
    setCalculatorData({ solute: '', solvent: '', temperature: '' })
  }, [])
  
  return {
    activeTab,
    setActiveTab,
    loading,
    setLoading,
    error,
    setError,
    calculatorData,
    setCalculatorData,
    solubility,
    setSolubility,
    smiles,
    setSmiles,
    predictedLogS,
    setPredictedLogS,
    predictedLogSEnsemble,
    setPredictedLogSEnsemble,
    formData,
    setFormData,
    estimatedSolubility,
    setEstimatedSolubility,
    adjustedSolubility,
    setAdjustedSolubility,
    clearAllStates,
  }
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================
export const SolubilityTabs = () => {
  const state = useSolubilityState()
  const apiCalls = useApiCalls()
  const { userId, isTokenAuth } = useSelector((state) => state.auth)

  const handleTabSwitch = useCallback((event, newValue) => {
    state.setActiveTab(newValue)
    state.clearAllStates()
  }, [state])

  const handleCalculate = useCallback(async () => {
    const { solute, solvent, temperature } = state.calculatorData
    
    const validationError = validateCalculatorInputs(solute, solvent, temperature)
    if (validationError) {
      state.setError(validationError)
      return
    }

    state.setLoading(true)
    state.setError('')
    state.setSolubility(null)

    if (userId) {
        apiCalls.logUserEvent({
            userId,
            eventType: 'ACTION',
            eventName: 'CALCULATE_SOLUBILITY_SOLVENT',
            metadata: {
                solute,
                solvent,
                temperature,
                token_auth: isTokenAuth || false
            },
            timestamp: new Date().toISOString()
        })
    }
    
    try {
      const result = await apiCalls.calculateSolventSoluteSolubility(
        solute,
        solvent,
        parseFloat(temperature)
      )
      
      if (result?.solubility !== undefined) {
        state.setSolubility(result.solubility)
      } else {
        throw new Error('Invalid response format')
      }
    } catch (err) {
      console.error('Solubility calculation error:', err)
      state.setError(err.message || 'Failed to calculate solubility. Please check your inputs and try again.')
    } finally {
      state.setLoading(false)
    }
  }, [state.calculatorData.solute, state.calculatorData.solvent, state.calculatorData.temperature, state.setLoading, state.setError, state.setSolubility, apiCalls.calculateSolventSoluteSolubility])

  const handlePredict = useCallback(async () => {
    const validationError = validateSmilesInput(state.smiles)
    if (validationError) {
      state.setError(validationError)
      return
    }

    state.setLoading(true)
    state.setError('')
    state.setPredictedLogS(null)
    state.setPredictedLogSEnsemble(null)

    if (userId) {
        apiCalls.logUserEvent({
            userId,
            eventType: 'ACTION',
            eventName: 'PREDICT_WATER_SOLUBILITY',
            metadata: {
                smiles: state.smiles.trim(),
                token_auth: isTokenAuth || false
            },
            timestamp: new Date().toISOString()
        })
    }
    
    try {
      const response = await apiCalls.calculateWaterSolubility({
        smiles: [state.smiles.trim()]
      })
      
      // Handle both possible response formats for robustness
      let predictions, ensemblePredictions
      
      if (response?.data?.logS_Prediction !== undefined) {
        // Handle nested data structure
        predictions = response.data.logS_Prediction
        ensemblePredictions = response.data.logS_Prediction_Ensemble
      } else if (response?.logS_Prediction !== undefined) {
        // Handle direct response structure
        predictions = response.logS_Prediction
        ensemblePredictions = response.logS_Prediction_Ensemble
      } else {
        throw new Error('No predictions returned from the server')
      }
      
      if (predictions !== undefined) {
        state.setPredictedLogS(predictions)
        if (ensemblePredictions !== undefined) {
          state.setPredictedLogSEnsemble(ensemblePredictions)
        }
      } else {
        throw new Error('Invalid response format from prediction service')
      }
    } catch (error) {
      console.error('Prediction error:', error)
      state.setError(`Prediction failed: ${error.message || 'Unknown error'}`)
    } finally {
      state.setLoading(false)
    }
  }, [state.smiles, state.setLoading, state.setError, state.setPredictedLogS, state.setPredictedLogSEnsemble, apiCalls.calculateWaterSolubility])

  const handleSolubilitySubmit = useCallback(async (e) => {
    e.preventDefault()
    
    const validationError = validateFormData(state.formData)
    if (validationError) {
      state.setError(validationError)
      return
    }

    state.setLoading(true)
    state.setError('')
    state.setEstimatedSolubility(null)
    state.setAdjustedSolubility(null)

    if (userId) {
        apiCalls.logUserEvent({
            userId,
            eventType: 'ACTION',
            eventName: 'CALCULATE_SOLUBILITY_ADVANCED',
            metadata: {
                ...state.formData,
                token_auth: isTokenAuth || false
            },
            timestamp: new Date().toISOString()
        })
    }
    
    try {
      const response = await apiCalls.calculateSolubilityWithThermodynamics(state.formData)
      
      if (response) {
        state.setEstimatedSolubility(response.estimated_solubility)
        state.setAdjustedSolubility(response.adjusted_solubility)
      } else {
        throw new Error('Invalid response from server')
      }
    } catch (err) {
      console.error('Form submission error:', err)
      state.setError(err.message || 'Failed to calculate solubility. Please verify your inputs and try again.')
    } finally {
      state.setLoading(false)
    }
  }, [state.formData, state.setLoading, state.setError, state.setEstimatedSolubility, state.setAdjustedSolubility, apiCalls.calculateSolubilityWithThermodynamics])

  const updateCalculatorData = useCallback((field, value) => {
    state.setCalculatorData(prev => ({ ...prev, [field]: value }))
  }, [state.setCalculatorData])

  const updateFormData = useCallback((field, value) => {
    state.setFormData(prev => ({ ...prev, [field]: value }))
  }, [state.setFormData])

  // ============================================================================
  // MEMOIZED TAB COMPONENTS
  // ============================================================================
  const CalculatorTab = useMemo(() => (
    <TabSection>
      <Typography 
        variant="h6" 
        gutterBottom 
        sx={{ 
          color: '#374151', 
          fontWeight: 600, 
          mb: 3,
          textAlign: 'center' 
        }}
      >
        Calculate Solute-Solvent Solubility
      </Typography>
      
      <FormCard elevation={0}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <StyledTextField
              fullWidth
              label="Solute SMILES"
              value={state.calculatorData.solute}
              onChange={(e) => updateCalculatorData('solute', e.target.value)}
              placeholder="e.g., CCO for ethanol"
              helperText="Enter the SMILES notation for the solute compound"
              disabled={state.loading}
            />
            <StyledTextField
              fullWidth
              label="Solvent SMILES"
              value={state.calculatorData.solvent}
              onChange={(e) => updateCalculatorData('solvent', e.target.value)}
              placeholder="e.g., O for water"
              helperText="Enter the SMILES notation for the solvent"
              disabled={state.loading}
            />
            <StyledTextField
              fullWidth
              label="Temperature (°C)"
              type="number"
              value={state.calculatorData.temperature}
              onChange={(e) => updateCalculatorData('temperature', e.target.value)}
              placeholder="25"
              helperText="Temperature in Celsius"
              disabled={state.loading}
            />
            
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <ActionButton
                label="Calculate Solubility"
                onClick={handleCalculate}
                isLoading={state.loading}
                disabled={!state.calculatorData.solute || !state.calculatorData.solvent || !state.calculatorData.temperature}
                sx={{ minWidth: 200 }}
              />
            </Box>
            
            {state.solubility !== null && (
              <ResultCard severity="success">
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  ✓ Calculation Complete
                </Typography>
                <Typography variant="body1">
                  <strong>Calculated Solubility:</strong>{' '}
                  {typeof state.solubility === 'number' ? state.solubility.toFixed(4) : state.solubility} mg/mL
                </Typography>
                <Typography variant="caption" sx={{ mt: 1, display: 'block', opacity: 0.8 }}>
                  Solubility calculated using molecular interaction models
                </Typography>
              </ResultCard>
            )}
          </Box>
        </CardContent>
      </FormCard>
    </TabSection>
  ), [state.calculatorData, state.loading, state.solubility, handleCalculate, updateCalculatorData])

  const PredictorTab = useMemo(() => (
    <TabSection>
      <Typography 
        variant="h6" 
        gutterBottom 
        sx={{ 
          color: '#374151', 
          fontWeight: 600, 
          mb: 3,
          textAlign: 'center' 
        }}
      >
        Predict Water Solubility from SMILES
      </Typography>
      
      <FormCard elevation={0}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <StyledTextField
              fullWidth
              label="SMILES String"
              value={state.smiles}
              onChange={(e) => state.setSmiles(e.target.value)}
              placeholder="e.g., CC(C)CC1=CC=C(C=C1)C(C)C(=O)O"
              helperText="Enter a valid SMILES notation for water solubility prediction"
              multiline
              rows={3}
              disabled={state.loading}
            />
            
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <ActionButton
                label="Predict LogS"
                onClick={handlePredict}
                isLoading={state.loading}
                disabled={!state.smiles.trim()}
                sx={{ minWidth: 200 }}
              />
            </Box>
            
            {(state.predictedLogS !== null || state.predictedLogSEnsemble !== null) && (
              <ResultCard severity="info">
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  🔬 Prediction Results
                </Typography>
                {state.predictedLogS !== null && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      <strong>Predicted LogS:</strong>{' '}
                      {typeof state.predictedLogS === 'number' ? state.predictedLogS.toFixed(4) : state.predictedLogS}
                    </Typography>
                  </Box>
                )}
                {state.predictedLogSEnsemble !== null && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      <strong>Ensemble LogS:</strong>{' '}
                      {typeof state.predictedLogSEnsemble === 'number' ? state.predictedLogSEnsemble.toFixed(4) : state.predictedLogSEnsemble}
                    </Typography>
                  </Box>
                )}
                <Divider sx={{ my: 2, backgroundColor: 'rgba(30, 64, 175, 0.2)' }} />
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  <strong>Note:</strong> LogS represents the logarithm of aqueous solubility (mol/L).
                  More negative values indicate lower solubility.
                </Typography>
              </ResultCard>
            )}
          </Box>
        </CardContent>
      </FormCard>
    </TabSection>
  ), [state.smiles, state.loading, state.predictedLogS, state.predictedLogSEnsemble, state.setSmiles, handlePredict])

  const FormTab = useMemo(() => (
    <TabSection>
      <Typography 
        variant="h6" 
        gutterBottom 
        sx={{ 
          color: '#374151', 
          fontWeight: 600, 
          mb: 3,
          textAlign: 'center' 
        }}
      >
        Advanced Solubility Calculation
      </Typography>
      
      <FormCard elevation={0}>
        <CardContent sx={{ p: 4 }}>
          <Box
            component="form"
            onSubmit={handleSolubilitySubmit}
            sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
          >
            {Object.entries(FIELD_LABELS).map(([field, label]) => {
              const isRequired = ['soluteSmiles', 'solventSmiles', 'temperature'].includes(field)
              const isNumeric = ['temperature', 'ph', 'deltaHsol', 'pKa', 'SPure', 'molecularWeight'].includes(field)
              
              return (
                <StyledTextField
                  key={field}
                  fullWidth
                  label={label}
                  name={field}
                  value={state.formData[field]}
                  onChange={(e) => updateFormData(field, e.target.value)}
                  type={isNumeric ? 'number' : 'text'}
                  required={isRequired}
                  helperText={
                    field.includes('Smiles') 
                      ? 'SMILES notation' 
                      : isRequired 
                        ? 'Required field' 
                        : 'Optional parameter'
                  }
                  disabled={state.loading}
                  multiline={field.includes('Smiles')}
                  rows={field.includes('Smiles') ? 2 : 1}
                />
              )
            })}
            
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <ActionButton
                label="Calculate Advanced Solubility"
                type="submit"
                isLoading={state.loading}
                disabled={!state.formData.soluteSmiles || !state.formData.solventSmiles || !state.formData.temperature}
                sx={{ minWidth: 250 }}
              />
            </Box>
            
            {(state.estimatedSolubility !== null || state.adjustedSolubility !== null) && (
              <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {state.estimatedSolubility !== null && (
                  <ResultCard severity="warning">
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                      📊 Estimated Solubility
                    </Typography>
                    <Typography variant="body1">
                      <strong>Result:</strong> {state.estimatedSolubility}
                    </Typography>
                    <Typography variant="caption" sx={{ mt: 1, display: 'block', opacity: 0.8 }}>
                      Initial calculation based on molecular properties
                    </Typography>
                  </ResultCard>
                )}
                {state.adjustedSolubility !== null && (
                  <ResultCard severity="success">
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                      ✨ Adjusted Solubility (Final Result)
                    </Typography>
                    <Typography variant="body1">
                      <strong>Result:</strong> {state.adjustedSolubility}
                    </Typography>
                    <Typography variant="caption" sx={{ mt: 1, display: 'block', opacity: 0.8 }}>
                      Thermodynamically adjusted calculation including all parameters
                    </Typography>
                  </ResultCard>
                )}
              </Box>
            )}
          </Box>
        </CardContent>
      </FormCard>
    </TabSection>
  ), [state.formData, state.loading, state.estimatedSolubility, state.adjustedSolubility, handleSolubilitySubmit, updateFormData])

  return (
    <Box>
      {/* Professional Header */}
      <HeaderContainer elevation={0}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography 
              variant="h4" 
              component="h1" 
              sx={{ 
                fontWeight: 600, 
                marginBottom: 1,
                fontSize: { xs: '1.5rem', sm: '2rem' }
              }}
            >
              Solubility Analysis Suite
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                opacity: 0.9,
                fontSize: '1.1rem',
                lineHeight: 1.5,
                marginBottom: 2
              }}
            >
              Comprehensive molecular solubility prediction and calculation platform
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <FeatureChip label="SMILES Analysis" size="small" />
              <FeatureChip label="Thermodynamic Models" size="small" />
              <FeatureChip label="ML Predictions" size="small" />
            </Box>
          </Box>
          <Box sx={{ 
            display: { xs: 'none', md: 'block' },
            opacity: 0.3,
            fontSize: '4rem',
            fontWeight: 300
          }}>
            ⚗️
          </Box>
        </Box>
      </HeaderContainer>

      {/* Main Content */}
      <ContentContainer elevation={0}>
        <StyledTabs 
          value={state.activeTab} 
          onChange={handleTabSwitch} 
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: '#E5E7EB' }}
        >
          <Tab 
            value={TABS.CALCULATOR} 
            label={TAB_LABELS[TABS.CALCULATOR]}
            sx={{ py: 2 }}
          />
          <Tab 
            value={TABS.PREDICTOR} 
            label={TAB_LABELS[TABS.PREDICTOR]}
            sx={{ py: 2 }}
          />
          <Tab 
            value={TABS.FORM} 
            label={TAB_LABELS[TABS.FORM]}
            sx={{ py: 2 }}
          />
        </StyledTabs>
        
        {state.error && (
          <Alert 
            severity="error" 
            sx={{ 
              m: 3, 
              fontWeight: 500,
              '& .MuiAlert-message': {
                fontSize: '0.95rem'
              }
            }}
            onClose={() => state.setError('')}
          >
            {state.error}
          </Alert>
        )}
        
        {state.activeTab === TABS.CALCULATOR && CalculatorTab}
        {state.activeTab === TABS.PREDICTOR && PredictorTab}
        {state.activeTab === TABS.FORM && FormTab}
      </ContentContainer>
    </Box>
  )
}
